import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, FolderKanban, ListChecks, Bug, Wallet, Users,
  ShieldCheck, Sparkles, BarChart3, ClipboardList
} from "lucide-react";

const NAV = {
  admin: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/projects", icon: FolderKanban, label: "Projects" },
    { to: "/tasks", icon: ListChecks, label: "Tasks" },
    { to: "/issues", icon: Bug, label: "Issues" },
    { to: "/payments", icon: Wallet, label: "Payments" },
    { to: "/users", icon: Users, label: "Users" },
    { to: "/tester-scores", icon: BarChart3, label: "Tester Scores" },
    { to: "/audit", icon: ShieldCheck, label: "Audit Log" },
  ],
  manager: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/projects", icon: FolderKanban, label: "Projects" },
    { to: "/tasks", icon: ListChecks, label: "Tasks" },
    { to: "/issues", icon: Bug, label: "Issues" },
    { to: "/payments", icon: Wallet, label: "Payments" },
    { to: "/users", icon: Users, label: "Testers" },
    { to: "/tester-scores", icon: BarChart3, label: "Tester Scores" },
  ],
  freelancer: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/my-projects", icon: FolderKanban, label: "My Projects" },
    { to: "/tasks", icon: ClipboardList, label: "My Tasks" },
    { to: "/issues", icon: Bug, label: "My Issues" },
    { to: "/payments", icon: Wallet, label: "Earnings" },
    { to: "/score", icon: BarChart3, label: "Tester Score" },
  ],
  client: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Overview" },
    { to: "/projects", icon: FolderKanban, label: "My Projects" },
    { to: "/tasks", icon: ListChecks, label: "Tasks" },
    { to: "/issues", icon: Bug, label: "Reported Issues" },
  ],
};

/** Shared inner content – used in desktop fixed sidebar AND mobile drawer. */
export function SidebarContent({ onNavigate }) {
  const { user } = useAuth();
  const items = NAV[user?.role] || [];

  return (
    <div className="bg-slate-950 text-slate-300 flex flex-col h-full w-full" data-testid="sidebar">
      <div className="px-5 py-5 border-b border-slate-800 flex items-center gap-2">
        <div className="w-7 h-7 bg-cyan-500 rounded-sm flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-slate-950" strokeWidth={2.5} />
        </div>
        <div>
          <div className="font-display font-bold text-white text-[15px] leading-none">CogniTrust</div>
          <div className="text-[10px] text-slate-500 tracking-wider uppercase mt-0.5">AntroTest QA</div>
        </div>
      </div>

      <nav className="flex-1 py-3 overflow-y-auto" data-testid="sidebar-nav">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            onClick={onNavigate}
            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            data-testid={`nav-${it.to.replace(/^\//, "").replace(/\//g, "-") || "root"}`}
          >
            <it.icon className="w-4 h-4" strokeWidth={1.75} /> <span>{it.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-slate-800 py-3">
        <div className="px-5 pt-1 pb-1 text-[10px] text-slate-600 uppercase tracking-wider">Signed in</div>
        <div className="px-5 pb-3 text-xs">
          <div className="text-white font-medium truncate">{user?.name}</div>
          <div className="text-slate-500 truncate">{user?.email}</div>
        </div>
      </div>
    </div>
  );
}

/** Desktop-only fixed sidebar (hidden below md). */
export default function Sidebar() {
  return (
    <aside className="w-60 flex-shrink-0 hidden md:flex border-r border-slate-800">
      <SidebarContent />
    </aside>
  );
}
